'use client';

import { useSyncExternalStore } from 'react';

/**
 * URL atual, re-renderizando quando ela muda — sem importar router nenhum.
 *
 * Existe porque o app hospedeiro nem sempre re-renderiza a sidebar na troca de rota: se o
 * <SideBar> está num layout que não assina a localização, o `isActive` que o dev passou fica
 * congelado e o menu acende o item errado.
 */

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

let url = '';

/** Lido no getSnapshot, não guardado por um listener: assim o primeiro render já vem certo. */
const getSnapshot = () => {
	const atual = window.location.href;
	if (atual !== url) url = atual;
	return url;
};
const getServerSnapshot = () => '';

let instalado = false;

function instalar() {
	if (instalado) return;
	instalado = true;

	window.addEventListener('popstate', notify);
	window.addEventListener('hashchange', notify);

	const nav = (window as unknown as { navigation?: EventTarget }).navigation;
	if (nav) {
		// currententrychange cobre pushState/replaceState também — nada de patch onde dá para evitar
		nav.addEventListener('currententrychange', notify);
		return;
	}

	// Sem Navigation API, pushState/replaceState não emitem evento nenhum. Patch APENAS para
	// avisar: sempre chama o original e devolve o retorno dele, nunca engole nem lança.
	// (é o mesmo que o wouter faz no import dele — bloquear por aqui é que seria proibido)
	(['pushState', 'replaceState'] as const).forEach((metodo) => {
		const original = window.history[metodo];
		window.history[metodo] = function (this: History, ...args: Parameters<History['pushState']>) {
			const retorno = original.apply(this, args);
			notify();
			return retorno;
		};
	});
}

const subscribe = (l: () => void) => {
	instalar();
	listeners.add(l);
	return () => {
		listeners.delete(l);
	};
};

export function useCurrentUrl() {
	return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * A rota comparável de uma URL: o caminho, ou o que vem depois do `#` em hash router (`/#/rota`).
 * Sem barra no fim, para `/produtos` e `/produtos/` serem a mesma coisa.
 */
function rotaDe(u: URL) {
	const bruta = u.hash.startsWith('#/') ? u.hash.slice(1) : u.pathname;
	return bruta.replace(/\/+$/, '') || '/';
}

/**
 * `href` casa com `urlAtual`? Exato ou como prefixo de sub-rota: `/produtos` acende em
 * `/produtos/123/editar`, mas não em `/produtos-antigos` — a barra é obrigatória.
 * Aceita href relativo (`to='produtos'`), resolvido contra a URL atual.
 */
export function rotaCombina(href: string, urlAtual: string) {
	if (!urlAtual) return false;

	let alvo: URL;
	let atual: URL;
	try {
		atual = new URL(urlAtual);
		alvo = new URL(href, urlAtual);
	} catch {
		return false;
	}
	if (alvo.origin !== atual.origin) return false;

	const a = rotaDe(alvo);
	const b = rotaDe(atual);

	return b === a || (a !== '/' && b.startsWith(`${a}/`));
}
